/**
 * My Replays — the named user's own stored runs, with star/watch actions
 * (docs/BARRAGE_IMPLEMENTATION_PLAN.md "Replay + showcase"). Every validated run
 * lives at `runs/{runId}` as a tiny `{ seed, inputs }` record; this screen lists
 * the compact {@link RunSummary}s the backend hands back (score / date / mode /
 * death-family) and lets the user:
 *   - WATCH a run — loads the full {@link import('../core/types').RunRecord} via
 *     `services.getReplay(id)` and hands it up to the parent's `onWatch` callback
 *     (which owns the actual deterministic re-sim player; this screen never
 *     renders a game loop);
 *   - STAR / UNSTAR a run for their public showcase {@link
 *     import('./UserCard').UserCard}, quota-bounded by their tier via the pure
 *     {@link replayStarQuota} core helper. The star button disables at the cap.
 *
 * GATING: `getMyReplays` / `setStarredReplay` / `unstarReplay` are NAMED-ONLY and
 * throw {@link RequiresAccountError} for anonymous users; we catch that and show
 * the "log in to keep replays" tease rather than crashing. The parent only mounts
 * this for named users, but we defend anyway.
 *
 * ARCHITECTURE: presentational + thin orchestration — screen-local UI state only,
 * talks to the backend ONLY through the {@link GameServices} facade, holds no
 * per-frame state, imports nothing from `game`/Firebase. The server is the source
 * of truth for the starred set; after every star/unstar we re-read the profile so
 * the quota usage reflects the authoritative state.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AttackFamily, GameMode, RunRecord } from '../core/types';
import type { GameServices } from '../services/types';
import { RequiresAccountError } from '../services/types';
import type { ProfileTier, RunSummary, UserProfile } from '../services/dataModel';
import { replayStarQuota } from '../services/dataModel';
import { useT } from './i18n';

interface ReplaysScreenProps {
  /** The tier-aware data facade. Replay reads/writes here are NAMED-ONLY. */
  services: GameServices;
  /**
   * Invoked when the user taps Watch on a run, AFTER its full
   * {@link RunRecord} has been loaded via `services.getReplay(id)`. The parent
   * owns the deterministic replay player (re-running the record through the core
   * sim); this screen only fetches the record and hands it over.
   */
  onWatch: (record: RunRecord) => void;
  /** Back to the previous screen (parent owns navigation). */
  onBack: () => void;
  /** When true (inside the POPSTAR tab shell) suppress this screen's own back-header. */
  embedded?: boolean;
}

/** Local load lifecycle for the one-shot replays + profile fetch. */
type LoadState = 'loading' | 'ready' | 'error' | 'locked';

/** Human label per attack family, for the "died to" tag. */
const FAMILY_LABEL: Record<AttackFamily, string> = {
  walls: 'Walls',
  lasers: 'Lasers',
  turret: 'Turret',
  caro: 'Caro',
  missiles: 'Missiles',
  bombs: 'Bombs',
};

/** Human label per game mode (multiplayer is a placeholder). */
const MODE_LABEL: Record<GameMode, string> = {
  solo: 'Solo',
  multiplayer: 'Multiplayer',
};

/** Locale short date-time from epoch ms; defensive against bad values. */
function formatDate(epochMs: number): string {
  if (!Number.isFinite(epochMs) || epochMs <= 0) return '';
  return new Date(epochMs).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** mm:ss.S from milliseconds (e.g. 92_400 → "1:32.4"). */
function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0:00.0';
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const tenths = Math.floor((totalSeconds * 10) % 10);
  return `${minutes}:${seconds.toString().padStart(2, '0')}.${tenths}`;
}

export function ReplaysScreen({ services, onWatch, onBack, embedded = false }: ReplaysScreenProps) {
  const t = useT();
  const [state, setState] = useState<LoadState>('loading');
  const [error, setError] = useState<string | null>(null);

  const [replays, setReplays] = useState<RunSummary[]>([]);
  // The user's tier + currently-starred ids drive the quota UI; sourced from the
  // authoritative profile (re-read after every star/unstar).
  const [tier, setTier] = useState<ProfileTier>('free');
  const [starred, setStarred] = useState<Set<string>>(new Set());

  // Transient per-run action state: the run id currently being watched / toggled.
  const [watchingId, setWatchingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  /** Pull the user's replays + profile together (named-only network reads). */
  const load = useCallback(async () => {
    setState('loading');
    setError(null);
    try {
      const [runs, profile]: [RunSummary[], UserProfile] = await Promise.all([
        services.getMyReplays(),
        services.getProfile(),
      ]);
      setReplays(runs);
      setTier(profile.tier);
      setStarred(new Set(profile.starredReplayIds));
      setState('ready');
    } catch (err) {
      if (err instanceof RequiresAccountError) {
        setState('locked');
      } else {
        setError(err instanceof Error ? err.message : 'Could not load your replays.');
        setState('error');
      }
    }
  }, [services]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Show a transient confirmation toast. */
  const toast = useCallback((msg: string) => {
    setFlash(msg);
    window.setTimeout(() => setFlash((cur) => (cur === msg ? null : cur)), 2400);
  }, []);

  const quota = useMemo(() => replayStarQuota(tier), [tier]);
  const used = starred.size;
  const atCap = used >= quota;

  /** Load the full record for a run and hand it to the parent's replay player. */
  const onWatchRun = useCallback(
    async (id: string) => {
      setWatchingId(id);
      setError(null);
      try {
        const record = await services.getReplay(id);
        if (!record) {
          setError('That replay is no longer available.');
          return;
        }
        onWatch(record);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load that replay.');
      } finally {
        setWatchingId(null);
      }
    },
    [services, onWatch],
  );

  /** Star or unstar a run; server enforces the quota, then we re-read the profile. */
  const onToggleStar = useCallback(
    async (id: string, isStarred: boolean) => {
      // Guard the cap client-side too, so the disabled button can't be defeated.
      if (!isStarred && atCap) return;
      setTogglingId(id);
      setError(null);
      try {
        if (isStarred) {
          await services.unstarReplay(id);
          toast('Removed from your card.');
        } else {
          await services.setStarredReplay(id);
          toast('Starred on your card!');
        }
        // Re-read the authoritative starred set (and tier, in case it changed).
        const profile = await services.getProfile();
        setTier(profile.tier);
        setStarred(new Set(profile.starredReplayIds));
      } catch (err) {
        if (err instanceof RequiresAccountError) {
          setState('locked');
        } else {
          setError(err instanceof Error ? err.message : 'Could not update your card.');
        }
      } finally {
        setTogglingId(null);
      }
    },
    [services, atCap, toast],
  );

  return (
    <div className={'screen network-screen replays-screen' + (embedded ? ' is-embedded' : '')}>
      <style>{REPLAYS_CSS}</style>

      {!embedded && (
        <header className="network-head">
          <button className="btn btn-ghost network-back" onClick={onBack} type="button">
            ‹ Back
          </button>
          <h1 className="network-title">{t('replays.title')}</h1>
        </header>
      )}

      <p className="replays-tagline">
        {t('replays.tagline')}
      </p>

      {state === 'ready' && (
        <div className="replays-quota" aria-live="polite">
          <span className="quota-star" aria-hidden="true">
            ★
          </span>
          <span className="quota-count">
            {used} <span className="quota-of">/ {quota}</span> {t('replays.starredLabel')}
          </span>
          <span className="quota-tier">{tier} {t('replays.tierSuffix')}</span>
        </div>
      )}

      {error && state === 'ready' && (
        <p className="replays-error" role="alert">
          {error}
        </p>
      )}

      {state === 'loading' && (
        <p className="replays-status">{t('replays.loading')}</p>
      )}

      {state === 'locked' && (
        <section className="panel replays-locked">
          <p className="replays-lock-emoji" aria-hidden="true">
            🔒
          </p>
          <h2>Log in to keep replays</h2>
          <p className="replays-lock-body">
            Saved replays and your showcase card are part of the full account. Create
            one (or log in) to re-watch your runs and star your best.
          </p>
        </section>
      )}

      {state === 'error' && (
        <section className="panel replays-retry">
          <p className="replays-error">{error}</p>
          <button className="btn btn-ghost" type="button" onClick={() => void load()}>
            Retry
          </button>
        </section>
      )}

      {state === 'ready' &&
        (replays.length === 0 ? (
          <section className="panel replays-empty">
            <p className="replays-empty-emoji" aria-hidden="true">
              🎬
            </p>
            <p className="replays-status">
              {t('replays.empty')}
            </p>
          </section>
        ) : (
          <ul className="replay-list">
            {replays.map((run) => {
              const isStarred = starred.has(run.id);
              const starDisabled =
                togglingId === run.id || (!isStarred && atCap);
              return (
                <li
                  key={run.id}
                  className={'panel replay-card' + (isStarred ? ' is-starred' : '')}
                >
                  <div className="replay-meta">
                    <span className="replay-score">{run.score.toLocaleString()}</span>
                    <span className="replay-tags">
                      <span className="replay-tag replay-tag-mode">
                        {MODE_LABEL[run.mode]}
                      </span>
                      <span className="replay-tag">{formatDuration(run.durationMs)}</span>
                      {run.deathFamily && (
                        <span className="replay-tag replay-tag-death">
                          {t('family.' + run.deathFamily, FAMILY_LABEL[run.deathFamily])}
                        </span>
                      )}
                    </span>
                    <time
                      className="replay-date"
                      dateTime={
                        run.createdAt > 0
                          ? new Date(run.createdAt).toISOString()
                          : undefined
                      }
                    >
                      {formatDate(run.createdAt)}
                    </time>
                  </div>

                  <div className="replay-actions">
                    <button
                      className="btn btn-primary replay-watch"
                      type="button"
                      disabled={watchingId === run.id}
                      onClick={() => void onWatchRun(run.id)}
                    >
                      {watchingId === run.id ? t('common.loading') : t('common.watch')}
                    </button>
                    <button
                      className={
                        'btn replay-star ' + (isStarred ? 'btn-primary' : 'btn-ghost')
                      }
                      type="button"
                      aria-pressed={isStarred}
                      aria-label={isStarred ? 'Unstar this run' : 'Star this run'}
                      disabled={starDisabled}
                      title={
                        !isStarred && atCap
                          ? `Star limit reached (${quota}) — unstar one or upgrade your tier.`
                          : undefined
                      }
                      onClick={() => void onToggleStar(run.id, isStarred)}
                    >
                      {togglingId === run.id
                        ? '…'
                        : isStarred
                          ? t('replays.starred2')
                          : t('replays.star')}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        ))}

      {flash && (
        <div className="replays-toast" role="status">
          {flash}
        </div>
      )}
    </div>
  );
}

/**
 * Screen-scoped styles (nested under `.replays-screen` so nothing leaks). Shared
 * primitives (`.screen`, `.panel`, `.btn`, `.title`, `.subtitle`,
 * `.leaderboard-empty`, CSS vars) come from src/index.css; only replay-specific
 * layout lives here.
 */
const REPLAYS_CSS = `
.replays-screen { gap: var(--space-4); }

.replays-screen .replays-tagline {
  margin: -4px 0 0;
  width: min(440px, 92vw);
  color: var(--muted);
  font-size: 14px;
  line-height: 1.5;
}

/* ---- Starred quota pill (glass) ---- */
.replays-screen .replays-quota {
  display: flex;
  align-items: center;
  gap: 8px;
  width: min(440px, 92vw);
  padding: 10px 16px;
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-pill);
  box-shadow: var(--shadow-1);
  backdrop-filter: blur(var(--blur));
  -webkit-backdrop-filter: blur(var(--blur));
  font-size: 14px;
  color: var(--text-dim);
}
.replays-screen .quota-star {
  color: var(--gold);
  font-size: 16px;
  line-height: 1;
  text-shadow: 0 0 12px rgba(255, 209, 102, 0.45);
}
.replays-screen .quota-count {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--text);
}
.replays-screen .quota-of {
  color: var(--muted);
  font-weight: 600;
}
.replays-screen .quota-tier {
  margin-left: auto;
  text-transform: capitalize;
  font-size: 12px;
  letter-spacing: 0.06em;
  color: var(--accent-soft);
  background: var(--accent-tint);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-pill);
  padding: 3px 12px;
  font-weight: 700;
}

/* ---- Inline feedback / empty states ---- */
.replays-screen .replays-error {
  margin: 0;
  color: var(--danger);
  font-size: 13px;
  width: min(440px, 92vw);
  text-align: center;
}
.replays-screen .replays-status {
  color: var(--muted);
  margin: 0;
  text-align: center;
}

/* ---- Replay list + cards ---- */
.replays-screen .replay-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: min(440px, 92vw);
}
.replays-screen .replay-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 16px;
  transition: border-color var(--t) var(--ease), box-shadow var(--t) var(--ease),
    transform var(--t-fast) var(--ease);
}
.replays-screen .replay-card:hover {
  transform: translateY(-1px);
  border-color: var(--glass-border-strong);
  box-shadow: var(--shadow-2), inset 0 1px 0 var(--glass-hi);
}
.replays-screen .replay-card.is-starred {
  border-color: rgba(255, 209, 102, 0.45);
  box-shadow: var(--shadow-glass), 0 0 22px rgba(255, 209, 102, 0.16);
}
.replays-screen .replay-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.replays-screen .replay-score {
  font-size: 24px;
  font-weight: 800;
  color: var(--text);
  font-variant-numeric: tabular-nums;
  line-height: 1.05;
}
.replays-screen .replay-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.replays-screen .replay-tag {
  font-size: 12px;
  color: var(--text-dim);
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-pill);
  padding: 2px 10px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.replays-screen .replay-tag-mode {
  color: var(--accent-soft);
  border-color: var(--glass-border-strong);
}
.replays-screen .replay-tag-death {
  color: var(--accent-2);
  border-color: rgba(255, 79, 157, 0.3);
}
.replays-screen .replay-tag-death::before {
  content: '☠ ';
  opacity: 0.8;
}
.replays-screen .replay-date {
  font-size: 11px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* ---- Per-card actions ---- */
.replays-screen .replay-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 0 0 auto;
}
.replays-screen .replay-actions .btn {
  padding: 9px 16px;
  font-size: 14px;
  white-space: nowrap;
  border-radius: var(--radius-sm);
}
.replays-screen .replay-watch { min-width: 104px; }
.replays-screen .replay-star { min-width: 104px; }

/* ---- Locked / empty panels ---- */
.replays-screen .replays-locked,
.replays-screen .replays-empty {
  width: min(440px, 92vw);
  text-align: center;
}
.replays-screen .replays-lock-emoji,
.replays-screen .replays-empty-emoji {
  font-size: 40px;
  margin: 4px 0 8px;
}
.replays-screen .replays-locked h2 {
  text-transform: none;
  letter-spacing: 0;
  color: var(--text);
  font-size: 18px;
  margin: 0 0 8px;
}
.replays-screen .replays-lock-body {
  color: var(--muted);
  font-size: 14px;
  line-height: 1.45;
  margin: 0;
}
.replays-screen .replays-retry {
  width: min(440px, 92vw);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

/* ---- Confirmation toast (glass pill) ---- */
.replays-screen .replays-toast {
  position: fixed;
  left: 50%;
  bottom: calc(env(safe-area-inset-bottom) + 24px);
  transform: translateX(-50%);
  z-index: var(--z-toast);
  background: var(--panel-strong);
  color: var(--text);
  border: 1px solid var(--accent);
  border-radius: var(--radius-pill);
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: var(--shadow-3);
  backdrop-filter: blur(var(--blur));
  -webkit-backdrop-filter: blur(var(--blur));
  animation: pop-up var(--t-slow) var(--ease-out) both;
}

/* ---- Narrow phones: stack actions below meta ---- */
@media (max-width: 380px) {
  .replays-screen .replay-card {
    flex-direction: column;
    align-items: stretch;
  }
  .replays-screen .replay-actions {
    flex-direction: row;
  }
  .replays-screen .replay-actions .btn {
    flex: 1;
  }
}
`;
